import React from 'react';
import Navigation from '../tulote/Navigation';
import Footer from '../tulote/Footer';
import { Building2, Upload, MapPin, Users, BarChart3, Play, CheckCircle } from 'lucide-react';
import brandConfig from '../../config/brandConfig';

const ComoFuncionaInmobiliariasPage = () => {
  const dashboardUrl = brandConfig.dashboardUrl;

  const steps = [
    {
      icon: <Play className="w-8 h-8" />,
      title: '1. Registra tu inmobiliaria',
      description: 'Crea tu cuenta gratis en minutos. Solo necesitas tu correo electronico y los datos basicos de tu empresa.',
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: '2. Crea tu proyecto',
      description: 'Configura tu proyecto inmobiliario con nombre, ubicacion y detalles. Sube el mapa de tus lotes en formato GeoJSON.',
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    {
      icon: <Upload className="w-8 h-8" />,
      title: '3. Importa tus lotes',
      description: 'Sube tus archivos GeoJSON, Shapefile o KML con los poligonos de cada lote. El sistema los procesa automaticamente.',
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: '4. Gestiona clientes',
      description: 'Importa tu base de clientes desde Excel o CSV. Asigna lotes, registra pagos y da seguimiento desde el Dashboard.',
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: '5. Publica y vende',
      description: 'Tu proyecto queda visible para miles de buscadores de lotes. Comparte el enlace con clientes potenciales.',
      color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    },
  ];

  const benefits = [
    'Mapa interactivo con los lotes de tu proyecto',
    'Panel de control para gestionar ventas',
    'Importacion de clientes desde Excel/CSV',
    'Seguimiento de pagos y estados de lotes',
    'Enlace compartible para tus clientes',
    'Soporte para GeoJSON, Shapefile y KML',
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-4">
            <Building2 className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Para Inmobiliarias
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Publica tus proyectos, gestiona tus lotes y conecta con clientes potenciales desde una sola plataforma.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
            >
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-lg ${step.color} mb-4`}>
                {step.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {step.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Lo que incluye la plataforma
              </h2>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 dark:text-gray-400">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/40 dark:to-orange-800/40 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-orange-600 dark:text-orange-400 mb-2">100%</div>
              <div className="text-lg font-semibold text-orange-800 dark:text-orange-300 mb-2">Gratis para empezar</div>
              <div className="text-orange-700 dark:text-orange-400 text-sm">
                Crea tu cuenta y publica tu primer proyecto sin costo
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-orange-50 dark:bg-orange-900/20 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Comienza a publicar tus lotes hoy
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Registra tu inmobiliaria y sube tu primer proyecto en minutos.
          </p>
          <a
            href={`${dashboardUrl}/iniciar-sesion`}
            className="inline-flex items-center px-6 py-3 text-white font-semibold rounded-lg transition-colors"
            style={{ background: brandConfig.getPrimaryGradient() }}
          >
            <Play className="w-4 h-4 mr-2" />
            Comenzar Gratis
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ComoFuncionaInmobiliariasPage;
